"use client";
import { useToast, UseToastOptions } from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { defaultNotificationPreferences } from "./factories";
import { PreferencesFactory } from "./factories/preferences.factory";
import {
  NotificationPreferenceCategory,
  NotificationPreferencesDto,
} from "./types";

export function useNotificationPreferences(
  preferences: NotificationPreferencesDto,
  onSubmit: (preferences: NotificationPreferencesDto) => void,
  _isLoading?: boolean,
  isError?: boolean,
  isSuccess?: boolean,
) {
  const [preferenceFactory, setPreferenceFactory] =
    useState<PreferencesFactory>(new PreferencesFactory(preferences));
  const [notificationPreferences, setNotificationPreferences] = useState<
    NotificationPreferenceCategory[]
  >(preferenceFactory.fetchProps());
  const toast = useToast();

  useEffect(() => {
    setNotificationPreferences(preferenceFactory.fetchProps());
    return () => {
      setNotificationPreferences(preferenceFactory.fetchProps());
    };
  }, [notificationPreferences, preferenceFactory]);

  useEffect(() => {
    if (isError) {
      handleToast(true);
    }
    if (isSuccess) {
      handleToast(false);
    }
  }, [isError, isSuccess]);

  const handleUseDefaultSettings = () => {
    setPreferenceFactory(
      new PreferencesFactory(defaultNotificationPreferences),
    );
    setNotificationPreferences(preferenceFactory.fetchProps());
  };

  const handleSubmit = (_options?: UseToastOptions) => {
    onSubmit(preferenceFactory.convertToDto());
  };

  const handleToast = (error?: boolean) => {
    if (error) {
      return toast({
        title: "Something Went Wrong",
        description:
          "We couldn't update your preferences. Please try again later.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
    return toast({
      title: "Updated your preferences",
      description: "Your preferences have been updated",
      status: "success",
      duration: 5000,
      isClosable: true,
    });
  };

  const handleChange = (
    category: keyof NotificationPreferencesDto,
    subcategory: string,
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const { name, checked } = event.target;
    const newPreferences = preferenceFactory.handlePreferenceChange(
      category,
      subcategory,
      name as "email_enabled" | "push_notifications",
      checked,
    );
    setNotificationPreferences(newPreferences);
  };

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = event.target;
    const newNotificationPreferences =
      preferenceFactory.handleSelectAllCategoryPreferences(
        name as keyof NotificationPreferencesDto,
        checked,
      );
    setNotificationPreferences(newNotificationPreferences);
  };

  return {
    notificationPreferences,
    handleChange,
    handleSelectAll,
    handleSubmit,
    handleUseDefaultSettings,
    handleToast,
  };
}
